import { createFileRoute } from '@tanstack/react-router';
import { type CSSProperties, type FormEvent, useMemo, useState } from 'react';

import type { AssetCategory } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useAssetCategories,
  useCreateAssetCategory,
  useDeleteAssetCategory,
  useUpdateAssetCategory,
} from '../lib/queries';

export const Route = createFileRoute('/asset-categories')({
  component: AssetCategoriesPage,
});

function AssetCategoriesPage() {
  const companyId = useActiveCompanyId();
  const { data: rows = [], isPending } = useAssetCategories(companyId, false);
  const deleteCat = useDeleteAssetCategory(companyId);

  const [q, setQ] = useState('');
  const [editTarget, setEditTarget] = useState<AssetCategory | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (!q) return true;
        const ql = q.toLowerCase();
        return r.code.toLowerCase().includes(ql) || r.nameTh.toLowerCase().includes(ql);
      }),
    [rows, q],
  );

  const stats = {
    total: rows.length,
    active: rows.filter((r) => r.isActive).length,
    inactive: rows.filter((r) => !r.isActive).length,
  };

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'หมวดสินทรัพย์']}
        title="หมวดสินทรัพย์ (Asset Categories)"
        actions={
          <>
            <HelpButton guide="asset-categories" />
            <button className="btn" disabled>
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowAdd(true)} disabled={!companyId}>
              <Icon name="plus" size={14} /> เพิ่มหมวด
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            หมวดทั้งหมด
          </div>
          <div className="value tnum">
            {stats.total} <span className="unit">หมวด</span>
          </div>
          <div className="trend faint">
            ใช้งาน {stats.active} · ปิด {stats.inactive}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            กำหนด GL ครบ
          </div>
          <div className="value tnum">
            {stats.active} <span className="unit">หมวด</span>
          </div>
          <div className="trend faint">แต่ละหมวดมี cost / accDep / depExp / gain / loss</div>
        </div>
        <div className="kpi" style={{ background: 'rgba(26,86,166,0.04)' }}>
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            คำแนะนำ
          </div>
          <div className="value" style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.5 }}>
            แก้ <b>defaultLifeMonths</b> + <b>defaultSalvage</b> + บัญชี GL 5 ช่องต่อหมวด
            ระบบจะใช้ค่านี้เป็น default ตอนลงทะเบียนสินทรัพย์ใหม่
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-md"
            placeholder="ค้นหา รหัส / ชื่อหมวด"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
            แสดง {filtered.length} จาก {rows.length} หมวด
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>รหัส</th>
                <th>ชื่อหมวด</th>
                <th className="num">อายุ (เดือน)</th>
                <th className="num">ซาก (฿)</th>
                <th>Dr Cost</th>
                <th>Cr AccDep</th>
                <th>Dr DepExp</th>
                <th>Cr Gain</th>
                <th>Dr Loss</th>
                <th>สถานะ</th>
                <th style={{ width: 100 }}>จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: 20 }} className="muted">
                    กำลังโหลด…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: 20 }} className="muted">
                    ไม่มีข้อมูล
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id ?? r.code}>
                    <td className="code">
                      <b>{r.code}</b>
                    </td>
                    <td>
                      {r.nameTh}
                      {r.nameEn && (
                        <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                          · {r.nameEn}
                        </span>
                      )}
                    </td>
                    <td className="num tnum">
                      {r.defaultLifeMonths}
                      <span className="muted" style={{ fontSize: 11 }}>
                        {' '}
                        ({Math.round(r.defaultLifeMonths / 12)}ปี)
                      </span>
                    </td>
                    <td className="num tnum">{String(r.defaultSalvage)}</td>
                    <td className="code muted">{r.glCostCode}</td>
                    <td className="code muted">{r.glAccDepCode}</td>
                    <td className="code muted">{r.glDepExpenseCode}</td>
                    <td className="code muted">{r.glDisposalGainCode}</td>
                    <td className="code muted">{r.glDisposalLossCode}</td>
                    <td>
                      {r.isActive ? (
                        <span className="badge positive">
                          <span className="bdot" />
                          ใช้งาน
                        </span>
                      ) : (
                        <span className="badge neutral">
                          <span className="bdot" />
                          ปิด
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn sm" onClick={() => setEditTarget(r)} title="แก้ไข">
                          <Icon name="eye" size={11} />
                        </button>
                        <button
                          className="btn sm"
                          onClick={() => {
                            if (r.id && confirm(`ปิดใช้งานหมวด ${r.code}?`)) {
                              deleteCat.mutate(r.id);
                            }
                          }}
                          title="ปิดใช้งาน"
                          disabled={!r.isActive || !r.id}
                          style={{ color: 'var(--negative)' }}
                        >
                          <Icon name="x" size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && companyId && (
        <AssetCategoryFormModal
          mode="add"
          companyId={companyId}
          onClose={() => setShowAdd(false)}
        />
      )}
      {editTarget && companyId && (
        <AssetCategoryFormModal
          mode="edit"
          companyId={companyId}
          initial={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// Add / Edit modal
// ============================================================

interface ModalProps {
  mode: 'add' | 'edit';
  companyId: string;
  initial?: AssetCategory;
  onClose: () => void;
}

function AssetCategoryFormModal({ mode, companyId, initial, onClose }: ModalProps) {
  const createCat = useCreateAssetCategory(companyId);
  const updateCat = useUpdateAssetCategory(companyId);
  const isEdit = mode === 'edit';
  const mutation = isEdit ? updateCat : createCat;

  const [code, setCode] = useState(initial?.code ?? '');
  const [nameTh, setNameTh] = useState(initial?.nameTh ?? '');
  const [nameEn, setNameEn] = useState(initial?.nameEn ?? '');
  const [lifeMonths, setLifeMonths] = useState<number>(initial?.defaultLifeMonths ?? 60);
  const [salvage, setSalvage] = useState<string>(String(initial?.defaultSalvage ?? 1));
  const [glCostCode, setGlCostCode] = useState(initial?.glCostCode ?? '1210');
  const [glAccDepCode, setGlAccDepCode] = useState(initial?.glAccDepCode ?? '1218');
  const [glDepExpenseCode, setGlDepExpenseCode] = useState(initial?.glDepExpenseCode ?? '6610');
  const [glDisposalGainCode, setGlDisposalGainCode] = useState(
    initial?.glDisposalGainCode ?? '4310',
  );
  const [glDisposalLossCode, setGlDisposalLossCode] = useState(
    initial?.glDisposalLossCode ?? '6410',
  );
  const [isActive, setIsActive] = useState<boolean>(initial?.isActive ?? true);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    try {
      if (isEdit && initial?.id) {
        await updateCat.mutateAsync({
          id: initial.id,
          data: {
            nameTh: nameTh.trim(),
            nameEn: nameEn.trim() || null,
            defaultLifeMonths: lifeMonths,
            defaultSalvage: Number(salvage),
            glCostCode: glCostCode.trim(),
            glAccDepCode: glAccDepCode.trim(),
            glDepExpenseCode: glDepExpenseCode.trim(),
            glDisposalGainCode: glDisposalGainCode.trim(),
            glDisposalLossCode: glDisposalLossCode.trim(),
            isActive,
          },
        });
      } else {
        await createCat.mutateAsync({
          code: code.trim(),
          nameTh: nameTh.trim(),
          nameEn: nameEn.trim() || null,
          defaultLifeMonths: lifeMonths,
          defaultSalvage: Number(salvage),
          glCostCode: glCostCode.trim(),
          glAccDepCode: glAccDepCode.trim(),
          glDepExpenseCode: glDepExpenseCode.trim(),
          glDisposalGainCode: glDisposalGainCode.trim(),
          glDisposalLossCode: glDisposalLossCode.trim(),
          isActive,
        });
      }
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 100,
    padding: 20,
    overflowY: 'auto',
  };
  const box: CSSProperties = {
    background: '#fff',
    borderRadius: 10,
    width: '100%',
    maxWidth: 640,
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
    maxHeight: 'calc(100vh - 40px)',
    display: 'flex',
    flexDirection: 'column',
  };
  const sectionLabel: CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
    marginTop: 6,
    marginBottom: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
  const sectionBar: CSSProperties = {
    width: 3,
    height: 14,
    background: 'var(--brand)',
    borderRadius: 2,
  };
  const fieldLabel: CSSProperties = { fontSize: 12, marginBottom: 4, color: 'var(--text-muted)' };

  return (
    <div style={overlay} onMouseDown={onClose}>
      <form
        style={box}
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {isEdit ? `แก้ไขหมวด ${initial?.code}` : 'เพิ่มหมวดสินทรัพย์ใหม่'}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose} title="ปิด">
            <Icon name="x" size={14} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'grid', gap: 14, overflowY: 'auto' }}>
          <div style={sectionLabel}>
            <span style={sectionBar} />
            ข้อมูลหมวด
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
            <label>
              <div style={fieldLabel}>รหัส *</div>
              <input
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="เช่น MACHINE"
                required
                disabled={isEdit}
                style={{ width: '100%' }}
              />
              {isEdit && (
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  รหัสแก้ไม่ได้
                </div>
              )}
            </label>
            <label>
              <div style={fieldLabel}>ชื่อหมวด (ไทย) *</div>
              <input
                className="input"
                value={nameTh}
                onChange={(e) => setNameTh(e.target.value)}
                placeholder="เช่น เครื่องจักรการผลิต"
                required
                style={{ width: '100%' }}
              />
            </label>
          </div>
          <label>
            <div style={fieldLabel}>ชื่อหมวด (อังกฤษ)</div>
            <input
              className="input"
              value={nameEn ?? ''}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="เช่น Machinery"
              style={{ width: '100%' }}
            />
          </label>

          <div style={sectionLabel}>
            <span style={sectionBar} />
            ค่า default สำหรับสินทรัพย์ในหมวดนี้
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              <div style={fieldLabel}>อายุการใช้งาน (เดือน) *</div>
              <input
                type="number"
                className="input"
                value={lifeMonths}
                onChange={(e) => setLifeMonths(Number(e.target.value))}
                min={1}
                max={600}
                required
                style={{ width: '100%', textAlign: 'right' }}
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                ≈ {Math.round(lifeMonths / 12)} ปี
              </div>
            </label>
            <label>
              <div style={fieldLabel}>มูลค่าซาก (บาท)</div>
              <input
                type="number"
                className="input"
                value={salvage}
                onChange={(e) => setSalvage(e.target.value)}
                min={0}
                step="0.01"
                required
                style={{ width: '100%', textAlign: 'right' }}
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                Thai SOP: ฿1 เพื่อรักษาสินทรัพย์ในบัญชี
              </div>
            </label>
          </div>

          <div style={sectionLabel}>
            <span style={sectionBar} />
            บัญชี GL ที่ใช้ใน JV
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: -8 }}>
            ใส่รหัสบัญชี 4 หลักจากผังบัญชี — ระบบจะใช้บัญชีเหล่านี้ตอนโพสต์ JV ของสินทรัพย์ในหมวดนี้
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <label>
              <div style={fieldLabel}>Dr Cost — ราคาทุน *</div>
              <input
                className="input"
                value={glCostCode}
                onChange={(e) => setGlCostCode(e.target.value)}
                placeholder="1210"
                required
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={fieldLabel}>Cr AccDep — ค่าเสื่อมสะสม *</div>
              <input
                className="input"
                value={glAccDepCode}
                onChange={(e) => setGlAccDepCode(e.target.value)}
                placeholder="1218"
                required
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={fieldLabel}>Dr DepExpense — ค่าเสื่อมราคา *</div>
              <input
                className="input"
                value={glDepExpenseCode}
                onChange={(e) => setGlDepExpenseCode(e.target.value)}
                placeholder="6610"
                required
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={fieldLabel}>Cr Gain — กำไรจากจำหน่าย *</div>
              <input
                className="input"
                value={glDisposalGainCode}
                onChange={(e) => setGlDisposalGainCode(e.target.value)}
                placeholder="4310"
                required
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={fieldLabel}>Dr Loss — ขาดทุนจากจำหน่าย *</div>
              <input
                className="input"
                value={glDisposalLossCode}
                onChange={(e) => setGlDisposalLossCode(e.target.value)}
                placeholder="6410"
                required
                style={{ width: '100%' }}
              />
            </label>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>ใช้งาน (แสดงในตัวเลือกตอนลงทะเบียนสินทรัพย์)</span>
          </label>

          {err && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: 'var(--negative)',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafbfc',
          }}
        >
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" className="btn primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'กำลังบันทึก…' : isEdit ? 'บันทึก' : '+ เพิ่มหมวด'}
          </button>
        </div>
      </form>
    </div>
  );
}
